import React from "react";
import PropTypes from "prop-types";
import Table from "../../components/common/table/Table";
import MessagePopOver from "../message-popOver/MessagePopOver";
import cx from "classnames";
import {
  updateRowData,
  ToggleField,
  getScaledFields,
  convertDateFormat,
  preprocessAriData,
} from "./TabularViewUtils";
import { getTaxPopOvermessage } from "../calendar-view/right-panel/TaxInfo";

const { Column, HeaderCell, Cell } = Table;

// ---------------------------------------------------------------------------
// Inner render helpers (lifted from TabularView.renderField / renderColumns /
// renderExcludeCell).  They live at module scope and receive everything they
// need as arguments so they stay pure / easily testable.
// ---------------------------------------------------------------------------

const renderField = (
  rowData,
  field,
  handleFieldChange,
  isEditable,
  handleToggleChange
) => {
  const { adultPrices } = rowData;

  switch (field.type) {
    case "text":
      return (
        <div
          className={cx({
            "right-align-table-tax": field.key === "sourceTax",
          })}
        >
          {field.key === "date"
            ? convertDateFormat(rowData[field.key], rowData)
            : rowData[field.key]}
          {field.key === "sourceTax" &&
            rowData[field.key] !== null &&
            !rowData.isCustomRow && (
              <MessagePopOver
                className="tax-info-icon"
                message={getTaxPopOvermessage(adultPrices)}
              />
            )}
        </div>
      );
    case "input":
      // For non-editable properties, render as read-only text
      if (!isEditable) {
        return (
          <div className="manageari-readonly" data-testid="readonly-field">
            {rowData[field.key] ?? ""}
          </div>
        );
      }
      return (
        <input
          type="text"
          value={rowData[field.key] ?? ""}
          onChange={(e) => {
            const value = e.target.value;
            handleFieldChange(
              e,
              Number.parseInt(value, 10),
              rowData.roomtypeId,
              field.key,
              rowData.date
            );
          }}
          className="manageari-input"
        />
      );
    case "inputDecimal":
      // For non-editable properties, render as read-only text
      if (!isEditable) {
        return (
          <div className="manageari-readonly" data-testid="readonly-field">
            {rowData[field.key] ?? ""}
          </div>
        );
      }
      return (
        <input
          type="text"
          value={rowData[field.key] ?? ""}
          onChange={(e) => {
            const value = e.target.value;
            handleFieldChange(
              e,
              value,
              rowData.roomtypeId,
              field.key,
              rowData.date
            );
          }}
          className="manageari-input"
        />
      );
    case "toggle":
      // For non-editable properties, render toggle as read-only text
      if (!isEditable) {
        return (
          <div className="manageari-readonly" data-testid="readonly-field">
            {rowData[field.key] ? "Open" : "Closed"}
          </div>
        );
      }
      return (
        <ToggleField
          rowData={rowData}
          handleToggleChange={handleToggleChange}
        />
      );
    default:
      return null;
  }
};

const renderColumns = (
  handleFieldChange,
  isEditable,
  containerWidth,
  handleToggleChange
) => {
  const scaledFields = getScaledFields(isEditable, containerWidth);

  return scaledFields.map((field) => {
    return (
      <Column key={field.key} width={field.width} data-testid="columnheader">
        <HeaderCell className="manageari-header" dataKey={field.key}>
          {field.label}
        </HeaderCell>
        <Cell dataKey={field.key}>
          {(rowData) =>
            renderField(
              rowData,
              field,
              handleFieldChange,
              isEditable,
              handleToggleChange
            )
          }
        </Cell>
      </Column>
    );
  });
};

// ---------------------------------------------------------------------------
// TabularViewTable component
// ---------------------------------------------------------------------------

const TabularViewTable = ({
  ariData,
  currentData,
  selectedRows,
  setCurrentData,
  setSelectedRows,
  isEditable,
  containerWidth,
  currentPage,
  setCurrentPage,
  pageSize,
  containerRef,
  selectedRoomtype,
  dateRange,
  defaultCurrency,
  handleToggleChange,
  handleCheckboxChange,
}) => {
  const handleFieldChange = (event, value, roomtypeId, fieldKey, date) => {
    // Number(value) mirrors global isNaN's coercion: value is a number from
    // the numeric fields but a raw string from the price field.
    let refinedValue = Number.isNaN(Number(value)) ? "" : value;
    const input = event.target;
    const cursorPosition = input.selectionStart;

    setCurrentData((prevData) =>
      updateRowData({
        prevData,
        roomtypeId,
        fieldKey,
        date,
        value: refinedValue,
        isEditable,
        ariData,
      })
    );
    // Restore the cursor position
    setTimeout(() => {
      input.setSelectionRange(cursorPosition, cursorPosition);
    }, 5);
    const isDateSelected = selectedRows.some((row) => row.date === date);
    if (!isDateSelected) {
      setSelectedRows((prevSelectedRows) => {
        return [...prevSelectedRows, { date: date }];
      });
    }
  };

  const renderExcludeCell = (rowData) => {
    if (rowData.isCustomRow) {
      return <>Bulk update:</>;
    }
    return (
      <input
        className="manageari-checkbox"
        type="checkbox"
        data-testid="checkbox"
        checked={selectedRows.some((row) => row.date === rowData.date)}
        onChange={(e) => handleCheckboxChange(e.target.checked, rowData)}
      />
    );
  };

  const allData = preprocessAriData({
    data: ariData,
    currentData,
    isEditable,
    selectedRoomtype,
    dateRange,
    defaultCurrency,
  });

  // Separate dummy row from regular rows for pagination
  const dummyRow = allData.find((row) => row.isCustomRow);
  const regularRows = allData.filter((row) => !row.isCustomRow);

  // Calculate pagination
  const totalPages = Math.ceil(regularRows.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedRows = regularRows.slice(startIndex, endIndex);

  // Combine dummy row (if exists) with paginated rows
  const tableData = dummyRow ? [dummyRow, ...paginatedRows] : paginatedRows;

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll table to top when changing pages
    if (containerRef.current) {
      const tableElement =
        containerRef.current.querySelector(".manageari-table");
      if (tableElement) {
        tableElement.scrollTop = 0;
      }
    }
  };

  return (
    <Table
      height={500}
      data={tableData}
      className="manageari-table"
      bordered
      cellBordered
      rowClassName={(rowData) => rowData?.isCustomRow && "custom-row"}
      paginated={regularRows.length > 0 && totalPages > 1}
      total={regularRows.length}
      currentPage={currentPage}
      onPageChange={handlePageChange}
      defaultPageSize={pageSize}
      maxButtons={3}
    >
      {/* Column Definitions */}
      {isEditable && (
        <Column width={90} align="center" data-testid="columnheader">
          <HeaderCell className="manageari-header">
            Exclude
            <MessagePopOver
              message={
                "Ticking the checkbox will exclude the row from bulk updates."
              }
            />
          </HeaderCell>
          <Cell>{(rowData) => renderExcludeCell(rowData)}</Cell>
        </Column>
      )}
      {renderColumns(
        handleFieldChange,
        isEditable,
        containerWidth,
        handleToggleChange
      )}
    </Table>
  );
};

TabularViewTable.propTypes = {
  ariData: PropTypes.array.isRequired,
  currentData: PropTypes.array.isRequired,
  selectedRows: PropTypes.array.isRequired,
  setCurrentData: PropTypes.func.isRequired,
  setSelectedRows: PropTypes.func.isRequired,
  isEditable: PropTypes.bool.isRequired,
  containerWidth: PropTypes.number,
  currentPage: PropTypes.number.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  pageSize: PropTypes.number.isRequired,
  containerRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  selectedRoomtype: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dateRange: PropTypes.array,
  defaultCurrency: PropTypes.string,
  handleToggleChange: PropTypes.func.isRequired,
  handleCheckboxChange: PropTypes.func.isRequired,
};

export default TabularViewTable;
